/**
 * Enums do dominio e seus rotulos em portugues.
 *
 * As CHAVES sao estaveis e em ingles (ADR-020); o rotulo e dado de apresentacao.
 * Compartilhado entre API e web para que nao existam duas listas divergentes.
 */

// SUSPENDED e LEGADO (CERES-005): nenhuma transicao leva mais ate ele; o
// bloqueio reversivel e DISABLED e o terminal preservado e ARCHIVED. O valor
// permanece porque dados historicos podem carrega-lo.
export const USER_STATUS = ['INVITED', 'ACTIVE', 'SUSPENDED', 'DISABLED', 'ARCHIVED'] as const;
export type UserStatus = (typeof USER_STATUS)[number];
export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  INVITED: 'Convidado',
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  DISABLED: 'Desativado',
  ARCHIVED: 'Arquivado',
};

export const ACCESS_SCOPE = ['PLATFORM', 'ORGANIZATION', 'PARTNER', 'OWN'] as const;
export type AccessScope = (typeof ACCESS_SCOPE)[number];
export const ACCESS_SCOPE_LABEL: Record<AccessScope, string> = {
  PLATFORM: 'Toda a plataforma',
  ORGANIZATION: 'Organização interna',
  PARTNER: 'Parceiro comercial',
  OWN: 'Apenas registros próprios',
};

export const ACCOUNT_STATUS = [
  'PROSPECT',
  'CUSTOMER',
  'INACTIVE',
  'PARTNER',
  'SUPPLIER',
  'COMPETITOR',
  'BLOCKED',
] as const;
export type AccountStatus = (typeof ACCOUNT_STATUS)[number];
export const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  PROSPECT: 'Prospect',
  CUSTOMER: 'Cliente',
  INACTIVE: 'Inativo',
  PARTNER: 'Parceiro',
  SUPPLIER: 'Fornecedor',
  COMPETITOR: 'Concorrente',
  BLOCKED: 'Bloqueado',
};

/**
 * A MATURIDADE COMERCIAL comprovada da relação (CERES-025).
 *
 * Separada de `AccountStatus` de propósito: aquele campo diz o TIPO de relação
 * (parceiro, fornecedor, concorrente) e a situação do cadastro; este diz até
 * onde a relação comercial chegou. Misturar as duas coisas numa coluna só foi o
 * que fez toda empresa recém-cadastrada aparecer como "prospect".
 *
 * A ordem do array É a escada de maturidade.
 */
export const COMMERCIAL_CLASSIFICATION = [
  'SUSPECT',
  'PROSPECT',
  'LEAD',
  'QUALIFIED_LEAD',
  'OPPORTUNITY',
  'CUSTOMER',
] as const;
export type CommercialClassification = (typeof COMMERCIAL_CLASSIFICATION)[number];

export const COMMERCIAL_CLASSIFICATION_LABEL: Record<CommercialClassification, string> = {
  SUSPECT: 'Suspect',
  PROSPECT: 'Prospect',
  LEAD: 'Lead',
  QUALIFIED_LEAD: 'Lead qualificado',
  OPPORTUNITY: 'Oportunidade',
  CUSTOMER: 'Cliente',
};

/** A frase do cartão-filtro: o que aquele grupo significa. */
export const COMMERCIAL_CLASSIFICATION_HINT: Record<CommercialClassification, string> = {
  SUSPECT: 'Ainda não avaliadas',
  PROSPECT: 'Aderência confirmada',
  LEAD: 'Interesse ou necessidade',
  QUALIFIED_LEAD: 'Prontas para o Comercial',
  OPPORTUNITY: 'Negociação aberta',
  CUSTOMER: 'Relação conquistada',
};

export const ACCOUNT_SIZE_TIER = ['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'] as const;
export type AccountSizeTier = (typeof ACCOUNT_SIZE_TIER)[number];
export const ACCOUNT_SIZE_TIER_LABEL: Record<AccountSizeTier, string> = {
  MICRO: 'Microempresa',
  SMALL: 'Pequeno porte',
  MEDIUM: 'Médio porte',
  LARGE: 'Grande porte',
  ENTERPRISE: 'Enterprise',
};

export const CONTACT_ROLE_TYPE = [
  'DECISION_MAKER',
  'INFLUENCER',
  'USER',
  'SPONSOR',
  'OPERATIONAL',
  'TECHNICAL',
  'PROCUREMENT',
  'LEGAL',
  'DETRACTOR',
] as const;
export type ContactRoleType = (typeof CONTACT_ROLE_TYPE)[number];
export const CONTACT_ROLE_TYPE_LABEL: Record<ContactRoleType, string> = {
  DECISION_MAKER: 'Decisor',
  INFLUENCER: 'Influenciador',
  USER: 'Usuário',
  SPONSOR: 'Patrocinador',
  OPERATIONAL: 'Contato operacional',
  TECHNICAL: 'Técnico',
  PROCUREMENT: 'Compras',
  LEGAL: 'Jurídico',
  DETRACTOR: 'Detrator',
};

export const CONTACT_STATUS = ['ACTIVE', 'INACTIVE', 'LEFT_COMPANY'] as const;
export type ContactStatus = (typeof CONTACT_STATUS)[number];
export const CONTACT_STATUS_LABEL: Record<ContactStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  LEFT_COMPANY: 'Saiu da empresa',
};

export const CONSENT_STATUS = ['UNKNOWN', 'GRANTED', 'DENIED', 'WITHDRAWN'] as const;
export type ConsentStatus = (typeof CONSENT_STATUS)[number];
export const CONSENT_STATUS_LABEL: Record<ConsentStatus, string> = {
  UNKNOWN: 'Não informado',
  GRANTED: 'Concedido',
  DENIED: 'Negado',
  WITHDRAWN: 'Revogado',
};

export const SOURCE_CATEGORY = [
  'INBOUND',
  'OUTBOUND',
  'PARTNER',
  'REFERRAL',
  'EVENT',
  'MARKETING',
  'OTHER',
] as const;
export type SourceCategory = (typeof SOURCE_CATEGORY)[number];

export const STAGE_TYPE = ['OPEN', 'WON', 'LOST'] as const;
export type StageType = (typeof STAGE_TYPE)[number];
export const STAGE_TYPE_LABEL: Record<StageType, string> = {
  OPEN: 'Em andamento',
  WON: 'Fechada — ganha',
  LOST: 'Fechada — perdida',
};

export const OPPORTUNITY_TYPE = [
  'ONE_OFF_SERVICE',
  'FIXED_PROJECT',
  'STAFF_ALLOCATION',
  'RECURRING_SERVICE',
  'SOFTWARE_LICENSE',
  'BKPILOT_PLAN',
  'BKPILOT_CREDITS',
  'ENTERPRISE_ASSURANCE',
  'OTHER',
] as const;
export type OpportunityType = (typeof OPPORTUNITY_TYPE)[number];
/**
 * CRM-VAL-008 §12: "Projeto fechado" ao lado do status "ABERTA" lia-se como
 * "negocio fechado" — ou seja, o oposto do que a oportunidade estava dizendo. O
 * CODIGO do enum nao muda (nenhum dado se move); muda o rotulo, que e onde a
 * ambiguidade morava.
 */
export const OPPORTUNITY_TYPE_LABEL: Record<OpportunityType, string> = {
  ONE_OFF_SERVICE: 'Serviço pontual',
  FIXED_PROJECT: 'Projeto de escopo fechado',
  STAFF_ALLOCATION: 'Alocação profissional',
  RECURRING_SERVICE: 'Serviço recorrente',
  SOFTWARE_LICENSE: 'Licença de software',
  BKPILOT_PLAN: 'Plano BKPilot',
  BKPILOT_CREDITS: 'Créditos BKPilot',
  ENTERPRISE_ASSURANCE: 'Operação enterprise / BKPilot Assurance',
  OTHER: 'Outro tipo',
};

export const OPPORTUNITY_STATUS = ['OPEN', 'WON', 'LOST', 'FROZEN'] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUS)[number];
export const OPPORTUNITY_STATUS_LABEL: Record<OpportunityStatus, string> = {
  OPEN: 'Aberta',
  WON: 'Ganha',
  LOST: 'Perdida',
  FROZEN: 'Congelada',
};

export const OFFERING_FAMILY = [
  'SOFTWARE',
  'CREDITS',
  'PROJECT',
  'RECURRING_SERVICE',
  'ALLOCATION',
  'CONSULTING',
  'TRAINING',
  'PILOT',
] as const;
export type OfferingFamily = (typeof OFFERING_FAMILY)[number];
export const OFFERING_FAMILY_LABEL: Record<OfferingFamily, string> = {
  SOFTWARE: 'Software',
  CREDITS: 'Créditos',
  PROJECT: 'Projetos',
  RECURRING_SERVICE: 'Serviços recorrentes',
  ALLOCATION: 'Alocação',
  CONSULTING: 'Consultoria',
  TRAINING: 'Treinamento',
  PILOT: 'Pilotos',
};

export const ACTIVITY_TYPE = [
  'TASK',
  'CALL',
  'MEETING',
  'EMAIL',
  'REMINDER',
  'NOTE_LOG',
  // Tipos próprios, e não `NOTE_LOG`: "por onde falamos com esse cliente?" é a
  // primeira pergunta de qualquer retomada, e um registro genérico não responde.
  'WHATSAPP',
  'SMS',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPE)[number];
export const ACTIVITY_TYPE_LABEL: Record<ActivityType, string> = {
  TASK: 'Tarefa',
  CALL: 'Ligação',
  MEETING: 'Reunião',
  EMAIL: 'E-mail',
  REMINDER: 'Lembrete',
  NOTE_LOG: 'Registro',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
};

export const ACTIVITY_STATUS = ['PLANNED', 'DONE', 'CANCELED'] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUS)[number];
export const ACTIVITY_STATUS_LABEL: Record<ActivityStatus, string> = {
  PLANNED: 'Planejada',
  DONE: 'Concluída',
  CANCELED: 'Cancelada',
};

export const ACTIVITY_PRIORITY = ['LOW', 'NORMAL', 'HIGH'] as const;
export type ActivityPriority = (typeof ACTIVITY_PRIORITY)[number];
export const ACTIVITY_PRIORITY_LABEL: Record<ActivityPriority, string> = {
  LOW: 'Baixa',
  NORMAL: 'Normal',
  HIGH: 'Alta',
};

export const ACTIVITY_DIRECTION = ['INBOUND', 'OUTBOUND'] as const;
export type ActivityDirection = (typeof ACTIVITY_DIRECTION)[number];

export const PARTNER_STATUS = ['PROSPECT', 'ACTIVE', 'SUSPENDED', 'TERMINATED'] as const;
export type PartnerStatus = (typeof PARTNER_STATUS)[number];
export const PARTNER_STATUS_LABEL: Record<PartnerStatus, string> = {
  PROSPECT: 'Em avaliação',
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  TERMINATED: 'Encerrado',
};

/**
 * CRM-VAL-003 §20 — parceiro é PESSOA ou EMPRESA.
 *
 * Restringir parceria a CNPJ excluía o representante, o consultor e o
 * indicador — que são a maioria do canal.
 */
export const PARTNER_KIND = ['PERSON', 'COMPANY'] as const;
export type PartnerKind = (typeof PARTNER_KIND)[number];
export const PARTNER_KIND_LABEL: Record<PartnerKind, string> = {
  PERSON: 'Pessoa',
  COMPANY: 'Empresa',
};

/** CRM-VAL-003 §22. `OTHER` mantém o modelo extensível sem migração. */
export const PARTNER_MODALITY = [
  'REFERRAL',
  'SALES_REPRESENTATION',
  'CHANNEL',
  'RESELLER',
  'STRATEGIC',
  'OTHER',
] as const;
export type PartnerModality = (typeof PARTNER_MODALITY)[number];
export const PARTNER_MODALITY_LABEL: Record<PartnerModality, string> = {
  REFERRAL: 'Indicação',
  SALES_REPRESENTATION: 'Representação comercial',
  CHANNEL: 'Canal',
  RESELLER: 'Revenda',
  STRATEGIC: 'Parceria estratégica',
  OTHER: 'Outra',
};

export const PARTNER_CONTRACT_STATUS = ['DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED'] as const;
export type PartnerContractStatus = (typeof PARTNER_CONTRACT_STATUS)[number];
export const PARTNER_CONTRACT_STATUS_LABEL: Record<PartnerContractStatus, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Vigente',
  EXPIRED: 'Expirado',
  TERMINATED: 'Encerrado',
};

export const RESERVATION_STATE = [
  'REQUESTED',
  'IN_CLARIFICATION',
  'APPROVED_PENDING_ACTIVATION',
  'ACTIVE',
  'IN_CONFLICT',
  'EXTENDED',
  'RELEASED',
  'CONVERTED',
  'EXPIRED',
  'REJECTED',
] as const;
export type ReservationState = (typeof RESERVATION_STATE)[number];
export const RESERVATION_STATE_LABEL: Record<ReservationState, string> = {
  REQUESTED: 'Solicitada',
  IN_CLARIFICATION: 'Em esclarecimento',
  APPROVED_PENDING_ACTIVATION: 'Aprovada — aguardando ativação',
  ACTIVE: 'Ativa',
  IN_CONFLICT: 'Em conflito',
  EXTENDED: 'Prorrogada',
  RELEASED: 'Liberada',
  CONVERTED: 'Convertida',
  EXPIRED: 'Expirada',
  REJECTED: 'Rejeitada',
};

export const RESERVATION_SCOPE_TYPE = [
  'COMPANY',
  'CNPJ',
  'BUSINESS_UNIT',
  'DEPARTMENT',
  'ECONOMIC_GROUP',
  'PROJECT',
] as const;
export type ReservationScopeType = (typeof RESERVATION_SCOPE_TYPE)[number];
export const RESERVATION_SCOPE_TYPE_LABEL: Record<ReservationScopeType, string> = {
  COMPANY: 'Empresa',
  CNPJ: 'CNPJ específico',
  BUSINESS_UNIT: 'Unidade de negócio',
  DEPARTMENT: 'Departamento',
  ECONOMIC_GROUP: 'Grupo econômico',
  PROJECT: 'Projeto',
};

export const CONTRIBUTION_TYPE = [
  'QUALIFIED_REFERRAL',
  'ACTIVE_PARTICIPATION',
  'APPROVED_EXCEPTION',
  'FIRST_RENEWAL',
  'SECOND_RENEWAL_ONWARD',
] as const;
export type ContributionType = (typeof CONTRIBUTION_TYPE)[number];
export const CONTRIBUTION_TYPE_LABEL: Record<ContributionType, string> = {
  QUALIFIED_REFERRAL: 'Indicação qualificada',
  ACTIVE_PARTICIPATION: 'Participação ativa',
  APPROVED_EXCEPTION: 'Exceção formalmente aprovada',
  FIRST_RENEWAL: 'Primeira renovação',
  SECOND_RENEWAL_ONWARD: 'Segunda renovação em diante',
};

// ==================== comissionamento (CRM-VAL-004) =========================

/**
 * §36 — o ciclo de vida de um lançamento.
 *
 * `DEVIDA` é o estado do fluxo INTERNO (comissão de quem vende): o gatilho
 * aconteceu e o valor entra na apuração sem alçada. `ELEGIVEL` é o equivalente
 * do fluxo de PARCEIRO, que passa por apuração e aprovação. Dois ritos, dois
 * nomes — fundi-los faria "aprovada" significar coisas diferentes conforme
 * quem recebe.
 */
export const COMMISSION_ENTRY_STATUS = [
  'PREVISTA',
  'DEVIDA',
  'ELEGIVEL',
  'EM_APURACAO',
  'AGUARDANDO_APROVACAO',
  'APROVADA',
  'PAGA',
  'CANCELADA',
  'CONTESTADA',
  'ESTORNADA',
] as const;
export type CommissionEntryStatus = (typeof COMMISSION_ENTRY_STATUS)[number];

/** CERES-DP-009 (DP-07): situação de um recebimento previsto (parcela) da venda. */
export const RECEIVABLE_INSTALLMENT_STATUS = ['PREVISTA', 'PARCIAL', 'RECEBIDA', 'CANCELADA'] as const;
export type ReceivableInstallmentStatus = (typeof RECEIVABLE_INSTALLMENT_STATUS)[number];
export const COMMISSION_ENTRY_STATUS_LABEL: Record<CommissionEntryStatus, string> = {
  PREVISTA: 'Prevista',
  DEVIDA: 'Devida',
  ELEGIVEL: 'Elegível',
  EM_APURACAO: 'Em apuração',
  AGUARDANDO_APROVACAO: 'Aguardando aprovação',
  APROVADA: 'Aprovada',
  PAGA: 'Paga',
  CANCELADA: 'Cancelada',
  CONTESTADA: 'Contestada',
  ESTORNADA: 'Estornada',
};

/** §34 — o fato comercial ou financeiro que faz nascer a comissão. */
export const REVENUE_EVENT_KIND = [
  'PROPOSAL_SIGNED',
  'CONTRACT_SIGNED',
  'INVOICE_ISSUED',
  'INVOICE_PAID',
  'PAYMENT_RECEIVED',
  'FIRST_INSTALLMENT',
  'OTHER',
] as const;
export type RevenueEventKind = (typeof REVENUE_EVENT_KIND)[number];
export const REVENUE_EVENT_KIND_LABEL: Record<RevenueEventKind, string> = {
  PROPOSAL_SIGNED: 'Assinatura da proposta',
  CONTRACT_SIGNED: 'Assinatura do contrato',
  INVOICE_ISSUED: 'Emissão da nota fiscal',
  INVOICE_PAID: 'Pagamento da nota fiscal',
  PAYMENT_RECEIVED: 'Recebimento efetivo',
  FIRST_INSTALLMENT: 'Primeira mensalidade',
  OTHER: 'Outro evento',
};

/** §24 — como o parceiro é remunerado. Decide quais campos a ficha mostra. */
export const PARTNER_REMUNERATION_MODEL = [
  'NONE',
  'COMMISSION_PCT',
  'FIXED_AMOUNT',
  'RECURRING_FEE',
  'FEE_PLUS_COMMISSION',
  'SPECIAL_TABLE',
] as const;
export type PartnerRemunerationModel = (typeof PARTNER_REMUNERATION_MODEL)[number];
export const PARTNER_REMUNERATION_MODEL_LABEL: Record<PartnerRemunerationModel, string> = {
  NONE: 'Sem remuneração',
  COMMISSION_PCT: 'Comissão percentual',
  FIXED_AMOUNT: 'Valor fixo',
  RECURRING_FEE: 'Fee recorrente',
  FEE_PLUS_COMMISSION: 'Fee + comissão',
  SPECIAL_TABLE: 'Tabela/regras especiais',
};

export const FEE_PERIODICITY = ['MONTHLY', 'QUARTERLY', 'YEARLY'] as const;
export type FeePeriodicity = (typeof FEE_PERIODICITY)[number];
export const FEE_PERIODICITY_LABEL: Record<FeePeriodicity, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  YEARLY: 'Anual',
};

/** §41 — situação do contrato de parceria. */
export const COMMISSION_BASIS = [
  'GROSS_INVOICE_RECEIVED',
  'GROSS_INVOICE_ISSUED',
  'CONTRACT_VALUE',
] as const;
export type CommissionBasis = (typeof COMMISSION_BASIS)[number];
export const COMMISSION_BASIS_LABEL: Record<CommissionBasis, string> = {
  GROSS_INVOICE_RECEIVED: 'Valor bruto da NF recebida',
  GROSS_INVOICE_ISSUED: 'Valor bruto da NF emitida',
  CONTRACT_VALUE: 'Valor do contrato',
};

export const RESERVATION_EVENT_TYPE = [
  'REQUESTED',
  'CLARIFICATION_REQUESTED',
  'CLARIFIED',
  'APPROVED',
  'REJECTED',
  'ACTIVATED',
  'UPDATE_LOGGED',
  'EXTENDED',
  'CONFLICT_OPENED',
  'CONFLICT_RESOLVED',
  'RELEASED',
  'CONVERTED',
  'EXPIRED',
] as const;
export type ReservationEventType = (typeof RESERVATION_EVENT_TYPE)[number];
export const RESERVATION_EVENT_TYPE_LABEL: Record<ReservationEventType, string> = {
  REQUESTED: 'Reserva solicitada',
  CLARIFICATION_REQUESTED: 'Esclarecimento solicitado',
  CLARIFIED: 'Esclarecimento prestado',
  APPROVED: 'Reserva aprovada',
  REJECTED: 'Reserva rejeitada',
  ACTIVATED: 'Reserva ativada',
  UPDATE_LOGGED: 'Atualização registrada',
  EXTENDED: 'Reserva prorrogada',
  CONFLICT_OPENED: 'Conflito aberto',
  CONFLICT_RESOLVED: 'Conflito resolvido',
  RELEASED: 'Reserva liberada',
  CONVERTED: 'Reserva convertida',
  EXPIRED: 'Reserva expirada',
};

export const CONFLICT_REASON = [
  'DUPLICATE_CNPJ',
  'ECONOMIC_GROUP_OVERLAP',
  'ACTIVE_RESERVATION',
  'PRIOR_INTERNAL_OPPORTUNITY',
  'OTHER_CHANNEL_ACTIVATION',
  'OTHER',
] as const;
export type ConflictReason = (typeof CONFLICT_REASON)[number];
export const CONFLICT_REASON_LABEL: Record<ConflictReason, string> = {
  DUPLICATE_CNPJ: 'CNPJ duplicado',
  ECONOMIC_GROUP_OVERLAP: 'Sobreposição de grupo econômico',
  ACTIVE_RESERVATION: 'Reserva já ativa',
  PRIOR_INTERNAL_OPPORTUNITY: 'Oportunidade interna anterior',
  OTHER_CHANNEL_ACTIVATION: 'Ativação por outro canal',
  OTHER: 'Outro motivo',
};

export const CONFLICT_STATUS = ['OPEN', 'UNDER_REVIEW', 'RESOLVED', 'DISMISSED'] as const;
export type ConflictStatus = (typeof CONFLICT_STATUS)[number];
export const CONFLICT_STATUS_LABEL: Record<ConflictStatus, string> = {
  OPEN: 'Aberto',
  UNDER_REVIEW: 'Em análise',
  RESOLVED: 'Resolvido',
  DISMISSED: 'Descartado',
};

export const CONFLICT_RESOLUTION_TYPE = ['AWARD_SINGLE', 'SPLIT', 'DENY_ALL', 'DEFER'] as const;
export type ConflictResolutionType = (typeof CONFLICT_RESOLUTION_TYPE)[number];
export const CONFLICT_RESOLUTION_TYPE_LABEL: Record<ConflictResolutionType, string> = {
  AWARD_SINGLE: 'Atribuir a um único canal',
  SPLIT: 'Ratear entre as partes',
  DENY_ALL: 'Negar a todos',
  DEFER: 'Postergar decisão',
};

/** UFs brasileiras. */
export const BRAZILIAN_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;
export type BrazilianState = (typeof BRAZILIAN_STATES)[number];

// ==================== catálogo comercial (CERES-004) ========================

export const CATALOG_ITEM_KIND = ['PRODUCT', 'SERVICE'] as const;
export type CatalogItemKind = (typeof CATALOG_ITEM_KIND)[number];
export const CATALOG_ITEM_KIND_LABEL: Record<CatalogItemKind, string> = {
  PRODUCT: 'Produto',
  SERVICE: 'Serviço',
};

export const CATALOG_PRICING_MODEL = [
  'HOURLY',
  'FIXED_PROJECT',
  'MONTHLY',
  'SUBSCRIPTION',
  'PER_UNIT',
  'PACKAGE',
  'CUSTOM',
] as const;
export type CatalogPricingModel = (typeof CATALOG_PRICING_MODEL)[number];
export const CATALOG_PRICING_MODEL_LABEL: Record<CatalogPricingModel, string> = {
  HOURLY: 'Por hora',
  FIXED_PROJECT: 'Projeto fechado',
  MONTHLY: 'Mensal',
  SUBSCRIPTION: 'Assinatura',
  PER_UNIT: 'Por unidade',
  PACKAGE: 'Pacote',
  CUSTOM: 'Personalizado',
};

/**
 * Rótulo curto da unidade sugerida por modelo, para a interface preencher o
 * campo "unidade" quando a pessoa não informar. É sugestão, não regra.
 */
export const CATALOG_PRICING_MODEL_UNIT_HINT: Record<CatalogPricingModel, string> = {
  HOURLY: 'hora',
  FIXED_PROJECT: 'projeto',
  MONTHLY: 'mês',
  SUBSCRIPTION: 'assinatura',
  PER_UNIT: 'unidade',
  PACKAGE: 'pacote',
  CUSTOM: 'item',
};

export const CATALOG_BILLING_PERIODICITY = ['MONTHLY', 'QUARTERLY', 'YEARLY'] as const;
export type CatalogBillingPeriodicity = (typeof CATALOG_BILLING_PERIODICITY)[number];
export const CATALOG_BILLING_PERIODICITY_LABEL: Record<CatalogBillingPeriodicity, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  YEARLY: 'Anual',
};

/**
 * CERES-034 — SITUAÇÃO DA APROVAÇÃO COMERCIAL.
 *
 * Os rótulos abaixo são a resposta que a pessoa lê, e não o nome do estado:
 * "PENDING" não diz nada a quem vende; "Aguardando aprovação" diz. A interface
 * NUNCA mostra a chave.
 */
export const COMMERCIAL_APPROVAL_STATUS = [
  'NOT_REQUIRED',
  'DRAFT',
  'PENDING',
  'APPROVED',
  'CHANGES_REQUESTED',
  'REJECTED',
  'SUPERSEDED',
] as const;
export type CommercialApprovalStatus = (typeof COMMERCIAL_APPROVAL_STATUS)[number];

/** O que originou a necessidade de aprovação. */
export const COMMERCIAL_APPROVAL_TRIGGER = ['DISCOUNT', 'MANUAL_PRICE'] as const;
export type CommercialApprovalTrigger = (typeof COMMERCIAL_APPROVAL_TRIGGER)[number];

/** As decisões que quem tem alçada pode tomar sobre um pedido. */
export const COMMERCIAL_APPROVAL_DECISION = ['APPROVED', 'CHANGES_REQUESTED', 'REJECTED'] as const;
export type CommercialApprovalDecision = (typeof COMMERCIAL_APPROVAL_DECISION)[number];

/** De onde vem o valor comercial da oportunidade (CERES-004). */
export const OPPORTUNITY_VALUE_SOURCE = ['MANUAL', 'ITEMS'] as const;
export type OpportunityValueSource = (typeof OPPORTUNITY_VALUE_SOURCE)[number];
export const OPPORTUNITY_VALUE_SOURCE_LABEL: Record<OpportunityValueSource, string> = {
  MANUAL: 'Valor informado manualmente',
  ITEMS: 'Soma dos itens da oportunidade',
};

// ==========================================================================
// CERES-036 — REGISTRO DOCUMENTAL DE PROPOSTAS (Fase 2)
// ==========================================================================

/**
 * De onde veio a proposta.
 *
 * `EXTERNAL` é o documento produzido FORA do CERES e aqui registrado — o caso
 * desta fase. `CERES` é a proposta composta pelo motor de documento, que é a
 * capacidade `proposals` (DORMANT) e não é criada aqui.
 */
export const PROPOSAL_ORIGIN = ['EXTERNAL', 'CERES'] as const;
export type ProposalOrigin = (typeof PROPOSAL_ORIGIN)[number];
export const PROPOSAL_ORIGIN_LABEL: Record<ProposalOrigin, string> = {
  EXTERNAL: 'Documento externo',
  CERES: 'Gerada no CERES',
};

/**
 * Situação do registro e de cada versão.
 *
 * `SUPERSEDED` é da VERSÃO, nunca do registro inteiro: substituir é o que
 * acontece com um documento, e a proposta continua viva na versão seguinte.
 */
export const PROPOSAL_REGISTRY_STATUS = [
  'DRAFT',
  'READY',
  'SENT',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'CANCELED',
  'SUPERSEDED',
] as const;
export type ProposalRegistryStatus = (typeof PROPOSAL_REGISTRY_STATUS)[number];

/** Por onde a proposta saiu. O CERES não envia nesta fase: isto é um relato. */
export const PROPOSAL_SEND_CHANNEL = [
  'EMAIL',
  'WHATSAPP',
  'MEETING',
  'CUSTOMER_PORTAL',
  'OTHER',
] as const;
export type ProposalSendChannel = (typeof PROPOSAL_SEND_CHANNEL)[number];

/**
 * Os desfechos que se registram sobre a versão vigente.
 *
 * Reabrir uma proposta aceita ou cancelada está fora desta fase — por isso a
 * lista não tem "voltar para rascunho".
 */
export const PROPOSAL_OUTCOME = ['ACCEPTED', 'REJECTED', 'CANCELED', 'EXPIRED'] as const;
export type ProposalOutcome = (typeof PROPOSAL_OUTCOME)[number];

/**
 * O que aconteceu, na linguagem do histórico da proposta.
 *
 * Chave estável e fechada: a tela traduz, e nenhum nome de tabela, enum ou
 * evento técnico chega aos olhos de quem lê.
 */
export const PROPOSAL_HISTORY_KIND = [
  'PROPOSTA_REGISTRADA',
  'VERSAO_CRIADA',
  'ARQUIVO_ANEXADO',
  'METADADOS_ALTERADOS',
  'DIVERGENCIA_DECLARADA',
  'ENVIO_REGISTRADO',
  'ENVIO_SEM_APROVACAO',
  'SITUACAO_ALTERADA',
  'ACEITE_REGISTRADO',
  'RECUSA_REGISTRADA',
  'CANCELAMENTO_REGISTRADO',
  'EXPIRACAO_REGISTRADA',
  'VERSAO_SUBSTITUIDA',
  'PROPOSTA_ARQUIVADA',
  'PROPOSTA_RESTAURADA',
  'ARQUIVO_BAIXADO',
  // ---- CERES-039: a geração assistida usa a MESMA trilha ------------------
  //
  // Eventos novos na lista que já existe, e não uma segunda trilha: a ficha da
  // proposta mostra um histórico só, e quem procura "o que aconteceu com esta
  // proposta?" não deveria precisar saber se ela foi registrada ou gerada.
  'GERACAO_INICIADA',
  'BLOCO_GERADO_POR_IA',
  'BLOCO_EDITADO',
  'ESTRUTURA_EDITADA',
  'BLOCO_RESTAURADO',
  'PREVIA_GERADA',
  'CONTEXTO_ATUALIZADO',
  'VERSAO_FINALIZADA',
  // ---- CERES-043: o envio por e-mail, na mesma trilha ---------------------
  //
  // Três eventos porque são três fatos que se respondem em tempos diferentes:
  // alguém pediu, o provedor aceitou, o provedor recusou. Um evento só
  // ("enviado") apagaria a tentativa que falhou — justamente a que precisa
  // aparecer quando o cliente diz que não recebeu nada.
  'EMAIL_SOLICITADO',
  'EMAIL_ACEITO_PELO_PROVEDOR',
  'EMAIL_FALHOU',
  // ---- CERES-044: o acompanhamento depois do envio ------------------------
  'EMAIL_RESULTADO_INCERTO',
  'ACOMPANHAMENTO_REGISTRADO',
  'PROXIMA_ACAO_DEFINIDA',
  'PROXIMA_ACAO_CONCLUIDA',
  'NEGOCIACAO_REABERTA',
  // ---- CERES-045: a cópia editável em Word --------------------------------
  //
  // Três eventos porque são três fatos: alguém pediu a cópia de trabalho, o
  // arquivo da versão NASCEU, alguém o levou. Registrar toda baixa como
  // geração faria a trilha afirmar que o documento mudou quando nada mudou.
  'DOCX_RASCUNHO_EXPORTADO',
  'DOCX_VERSAO_GERADO',
  'DOCX_VERSAO_BAIXADO',
  // ---- CERES-PRP-011: destinatário e validade trocados no editor ----------
  'DADOS_DO_RASCUNHO_ALTERADOS',
] as const;
export type ProposalHistoryKind = (typeof PROPOSAL_HISTORY_KIND)[number];

// ===========================================================================
// CERES-038 — BIBLIOTECA DE MODELOS DE PROPOSTA
// ===========================================================================

/**
 * A situação do MODELO (o registro lógico), e não a de uma versão.
 *
 * Três estados porque são três perguntas diferentes: "ainda estou montando?",
 * "pode ser usado numa proposta?" e "saiu de circulação sem ser apagado?".
 * Arquivar não apaga: as propostas já feitas continuam apontando para a versão
 * que usaram, e apagar o modelo levaria junto a resposta para "com que modelo
 * isto foi feito?".
 */
export const PROPOSAL_TEMPLATE_STATUS = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export type ProposalTemplateStatus = (typeof PROPOSAL_TEMPLATE_STATUS)[number];

/**
 * Para que serve o modelo.
 *
 * Lista deliberadamente genérica: o CERES é um CRM B2B, e amarrar as finalidades
 * ao que a BugKillers vende faria toda outra organização escolher "outra".
 */
export const PROPOSAL_TEMPLATE_PURPOSE = [
  'SERVICES',
  'PRODUCT',
  'SUBSCRIPTION',
  'LICENSE',
  'OUTSOURCING',
  'PROJECT',
  'RENEWAL',
  'COMBINED',
  'GENERIC',
] as const;
export type ProposalTemplatePurpose = (typeof PROPOSAL_TEMPLATE_PURPOSE)[number];

/** Estratégia do conteúdo da proposta; nunca descreve aparência. */
export const PROPOSAL_PURPOSE = [
  'NEW_SALE',
  'UPSELL',
  'CROSS_SELL',
  'RENEWAL',
  'PROJECT',
  'RECURRING_SERVICE',
  'TECHNICAL',
  'EXECUTIVE',
  'INSTITUTIONAL',
  'COMBINED',
] as const;
export type ProposalPurpose = (typeof PROPOSAL_PURPOSE)[number];

/** Família visual do modelo. */
export const PROPOSAL_VISUAL_STYLE = [
  'EDITORIAL',
  'IMPACT',
  'EXECUTIVE',
  'ESSENTIAL',
  /** CERES-PRP-011: o quinto estilo oficial, antes um modelo de conteúdo igual aos outros. */
  'MINIMAL',
  'CUSTOM',
] as const;
export type ProposalVisualStyle = (typeof PROPOSAL_VISUAL_STYLE)[number];

/**
 * CERES-PRP-011 — OS CINCO ESTILOS OFICIAIS, na ordem em que a galeria os mostra.
 *
 * Estilo é SÓ aparência: capa, grid, tipografia, hierarquia, tabelas, faixas,
 * paginação e espaçamento. `CUSTOM` fica fora porque é o modelo que a própria
 * organização desenhou — ele não é um estilo, é um documento inteiro.
 */
export const PROPOSAL_OFFICIAL_STYLE = [
  'IMPACT',
  'EXECUTIVE',
  'EDITORIAL',
  'ESSENTIAL',
  'MINIMAL',
] as const;
export type ProposalOfficialStyle = (typeof PROPOSAL_OFFICIAL_STYLE)[number];

/**
 * CERES-PRP-011 — O PACOTE DE CONTEÚDO: a estrutura de blocos de uma proposta.
 *
 * Terceiro eixo, independente do estilo (aparência) e da finalidade (argumento).
 * "Projeto" e "Outsourcing" eram modelos com a mesma cara e o mesmo texto dos
 * demais; agora decidem quais seções o documento tem, sem decidir o visual.
 */
export const PROPOSAL_CONTENT_PACKAGE = [
  'PROJECT',
  'SERVICE',
  'OUTSOURCING',
  'PRODUCT',
  'LICENSE',
  'TECHNICAL',
] as const;
export type ProposalContentPackage = (typeof PROPOSAL_CONTENT_PACKAGE)[number];

/**
 * CERES-PRP-011 — DE ONDE VEIO O TEXTO DE UM BLOCO, e quanto se pode confiar nele.
 *
 * `CRM` é dado cadastrado (empresa, contato, composição); `BRIEFING` é o que foi
 * combinado na oportunidade; `CATALOGO` e `PLAYBOOK` são conhecimento de produto
 * adaptado para a proposta; `INSTITUCIONAL` é o texto da própria organização;
 * `IA` é sugestão de modelo de linguagem; `MANUAL` é o que uma pessoa escreveu.
 */
export const PROPOSAL_BLOCK_PROVENANCE = [
  'CRM',
  'BRIEFING',
  'CATALOGO',
  'PLAYBOOK',
  'INSTITUCIONAL',
  'MODELO',
  'IA',
  'MANUAL',
  'SEM_FONTE',
] as const;
export type ProposalBlockProvenance = (typeof PROPOSAL_BLOCK_PROVENANCE)[number];

/** `INFERIDA` = texto adaptado de uma fonte genérica; precisa de leitura antes de enviar. */
export const PROPOSAL_BLOCK_CONFIDENCE = ['ALTA', 'MEDIA', 'INFERIDA', 'SEM_FONTE'] as const;
export type ProposalBlockConfidence = (typeof PROPOSAL_BLOCK_CONFIDENCE)[number];

export const PROPOSAL_VISUAL_TRAIT = [
  'executivo',
  'editorial',
  'criativo',
  'minimalista',
  'alto-contraste',
  'institucional',
] as const;
export type ProposalVisualTrait = (typeof PROPOSAL_VISUAL_TRAIT)[number];

/**
 * Os tipos de bloco que o documento conhece.
 *
 * Fechada de propósito. Um "tipo livre" transformaria o modelo num editor de
 * HTML com outro nome, e o renderizador passaria a receber estrutura que não
 * sabe desenhar — que é como um documento chega torto ao cliente.
 */
export const PROPOSAL_BLOCK_KIND = [
  'COVER',
  'SUPPLIER',
  'CUSTOMER',
  'CONTEXT',
  'OBJECTIVES',
  'SOLUTION',
  'ITEMS',
  'SCOPE',
  'DELIVERABLES',
  'METHODOLOGY',
  'SCHEDULE',
  'TEAM',
  'INVESTMENT',
  'COMMERCIAL_TERMS',
  'ASSUMPTIONS',
  'RESPONSIBILITIES',
  'EXCLUSIONS',
  'CASES',
  'NEXT_STEPS',
  'VALIDITY',
  'ACCEPTANCE',
  'FREE_TEXT',
  /**
   * CERES-039 — TABELA SIMPLES.
   *
   * Não substitui a tabela comercial repetitiva: aquela lê os itens congelados
   * da proposta e não se digita. Esta é a matriz de responsabilidades, o
   * comparativo, o cronograma manual, o nível de serviço — conteúdo que o
   * modelo escreve e que não vem do Catálogo.
   */
  'SIMPLE_TABLE',
  'IMAGE',
  'PAGE_BREAK',
  /** CERES-PRP-011: a carta que abre a proposta, endereçada ao destinatário. */
  'COVER_LETTER',
  /** CERES-PRP-011: a situação do cliente hoje, antes do que se entende dela. */
  'CURRENT_SCENARIO',
] as const;
export type ProposalBlockKind = (typeof PROPOSAL_BLOCK_KIND)[number];

/** Alinhamento de uma coluna da tabela simples. */
export const PROPOSAL_TABLE_ALIGNMENT = ['ESQUERDA', 'CENTRO', 'DIREITA'] as const;
export type ProposalTableAlignment = (typeof PROPOSAL_TABLE_ALIGNMENT)[number];

/**
 * Limites da tabela simples.
 *
 * Existem para que ela não vire planilha: uma tabela de 40 colunas não cabe em
 * A4, e o documento sairia com texto cortado sem ninguém ver na edição.
 */
export const PROPOSAL_TABLE_LIMITES = {
  colunas: 12,
  linhas: 100,
  caracteresPorCelula: 500,
} as const;

/**
 * De onde sai o conteúdo do bloco.
 *
 * `AI_ASSISTED` existe na estrutura e NÃO é gerado nesta fase: reconhecer a
 * origem agora evita que a fase seguinte precise migrar todo modelo já
 * publicado. Um bloco assim se comporta como `EDITABLE` até lá — o que é a
 * verdade, e não uma promessa.
 */
export const PROPOSAL_BLOCK_SOURCE = ['FIXED', 'DYNAMIC', 'EDITABLE', 'AI_ASSISTED'] as const;
export type ProposalBlockSource = (typeof PROPOSAL_BLOCK_SOURCE)[number];

/**
 * Ênfase do bloco no documento. Semântica, nunca CSS livre.
 *
 * CERES-040 acrescentou `OBSERVACAO` e `AVISO` porque a hierarquia tipográfica
 * de uma proposta tem mais de três degraus: a ressalva de rodapé de seção e a
 * pendência que precisa saltar aos olhos vinham saindo com o mesmo peso do
 * texto corrido, e quem lê não distinguia uma da outra.
 */
export const PROPOSAL_BLOCK_STYLE = [
  'NORMAL',
  'HIGHLIGHT',
  'SUBTLE',
  'OBSERVACAO',
  'AVISO',
] as const;
export type ProposalBlockStyle = (typeof PROPOSAL_BLOCK_STYLE)[number];

/** Layouts de capa oferecidos. O usuário escolhe; não posiciona por coordenada. */
export const PROPOSAL_COVER_LAYOUT = [
  'INSTITUCIONAL',
  'MINIMALISTA',
  'IMAGEM_DESTAQUE',
  'FAIXA_LATERAL',
  /** CERES-PRP-011: bloco de cor no terço superior, com o título dentro dele. */
  'BLOCO_SUPERIOR',
] as const;
export type ProposalCoverLayout = (typeof PROPOSAL_COVER_LAYOUT)[number];

// ===========================================================================
// CERES-040 — A APRESENTAÇÃO DO DOCUMENTO
// ===========================================================================

/**
 * O alinhamento da capa.
 *
 * `DIREITA` entrou na CERES-040: com faixa lateral à esquerda, o texto alinhado
 * à esquerda encosta na faixa, e a capa sai desequilibrada sem nenhum erro de
 * conteúdo.
 */
export const PROPOSAL_COVER_ALIGNMENT = ['ESQUERDA', 'CENTRO', 'DIREITA'] as const;
export type ProposalCoverAlignment = (typeof PROPOSAL_COVER_ALIGNMENT)[number];

/**
 * Como a imagem ocupa o espaço que lhe cabe.
 *
 * Nenhum deles distorce: a proporção da imagem é preservada em todos, e o que
 * muda é o que acontece com a sobra — mostrar inteira, cortar o excesso,
 * esticar até a largura útil ou respeitar o tamanho natural. Uma quinta opção
 * que deformasse produziria logotipo achatado no documento do cliente, e
 * ninguém escolhe isso de propósito.
 */
export const PROPOSAL_IMAGE_FIT = ['CONTER', 'PREENCHER', 'LARGURA_DA_PAGINA', 'NATURAL'] as const;
export type ProposalImageFit = (typeof PROPOSAL_IMAGE_FIT)[number];

/** A largura da imagem, em frações da área útil — nunca em pixels. */
export const PROPOSAL_IMAGE_WIDTH = ['PEQUENA', 'MEDIA', 'GRANDE', 'TOTAL'] as const;
export type ProposalImageWidth = (typeof PROPOSAL_IMAGE_WIDTH)[number];

/** Alinhamento horizontal de imagem e de logotipo. */
export const PROPOSAL_IMAGE_ALIGNMENT = ['ESQUERDA', 'CENTRO', 'DIREITA'] as const;
export type ProposalImageAlignment = (typeof PROPOSAL_IMAGE_ALIGNMENT)[number];

/** O tamanho do logotipo no cabeçalho e na capa. Lista, e não milímetros. */
export const PROPOSAL_LOGO_SIZE = ['PEQUENO', 'MEDIO', 'GRANDE'] as const;
export type ProposalLogoSize = (typeof PROPOSAL_LOGO_SIZE)[number];

/**
 * O desenho dos títulos de seção.
 *
 * Quatro tratamentos fechados. Um editor de CSS no lugar disto faria cada
 * modelo inventar a sua própria hierarquia — e a segunda proposta da mesma
 * empresa sairia com outra cara.
 */
export const PROPOSAL_TITLE_STYLE = ['SOBRIO', 'FIO', 'FAIXA', 'CAIXA_ALTA'] as const;
export type ProposalTitleStyle = (typeof PROPOSAL_TITLE_STYLE)[number];

/** A escala tipográfica do documento, aplicada sobre o tamanho base. */
export const PROPOSAL_TYPE_SCALE = ['COMPACTA', 'NORMAL', 'AMPLA'] as const;
export type ProposalTypeScale = (typeof PROPOSAL_TYPE_SCALE)[number];

/** As bordas da tabela. */
export const PROPOSAL_TABLE_BORDER = ['NENHUMA', 'HORIZONTAIS', 'COMPLETAS'] as const;
export type ProposalTableBorder = (typeof PROPOSAL_TABLE_BORDER)[number];

/** O respiro interno das células. */
export const PROPOSAL_TABLE_PADDING = ['COMPACTO', 'NORMAL', 'CONFORTAVEL'] as const;
export type ProposalTablePadding = (typeof PROPOSAL_TABLE_PADDING)[number];

/**
 * O formato da paginação no rodapé.
 *
 * `PAGINA_X_DE_Y` só é honesto porque o gerador escreve o rodapé no FIM, com
 * todas as páginas já criadas: um total calculado durante o fluxo estaria
 * errado na última quebra, e o documento diria "3 de 4" numa proposta de cinco
 * páginas.
 */
export const PROPOSAL_PAGE_NUMBER_FORMAT = [
  'NENHUM',
  'NUMERO',
  'PAGINA_X',
  'PAGINA_X_DE_Y',
] as const;
export type ProposalPageNumberFormat = (typeof PROPOSAL_PAGE_NUMBER_FORMAT)[number];

/**
 * As fontes que o gerador sabe desenhar com acentuação correta.
 *
 * Lista controlada, e não campo livre: uma fonte que o PDF não tem transforma
 * "Fundição" em caixinhas no documento que vai ao cliente.
 */
export const PROPOSAL_TEMPLATE_FONT = ['HELVETICA', 'TIMES', 'COURIER'] as const;
export type ProposalTemplateFont = (typeof PROPOSAL_TEMPLATE_FONT)[number];

/** Como o modelo se aplica ao que está sendo vendido. */
export const PROPOSAL_TEMPLATE_SCOPE = ['GENERIC', 'KIND', 'CATEGORY', 'ITEM'] as const;
export type ProposalTemplateScope = (typeof PROPOSAL_TEMPLATE_SCOPE)[number];

/** O que aconteceu com um modelo, na linguagem do histórico. */
export const PROPOSAL_TEMPLATE_HISTORY_KIND = [
  'MODELO_CRIADO',
  'MODELO_ALTERADO',
  'BLOCOS_ALTERADOS',
  'IDENTIDADE_ALTERADA',
  'MODELO_DUPLICADO',
  'VERSAO_PUBLICADA',
  'VERSAO_CRIADA',
  'DEFINIDO_COMO_PADRAO',
  'PADRAO_REMOVIDO',
  'MODELO_ARQUIVADO',
  'MODELO_RESTAURADO',
] as const;
export type ProposalTemplateHistoryKind = (typeof PROPOSAL_TEMPLATE_HISTORY_KIND)[number];

// ===========================================================================
// CERES-039 — GERAÇÃO ASSISTIDA DE PROPOSTA
// ===========================================================================

/** A situação do rascunho de geração. */
export const PROPOSAL_DRAFT_STATUS = ['OPEN', 'FINALIZED', 'DISCARDED'] as const;
export type ProposalDraftStatus = (typeof PROPOSAL_DRAFT_STATUS)[number];

/**
 * O estado de UM bloco do rascunho — o que a tela mostra ao lado do nome.
 *
 * Seis estados porque são seis perguntas diferentes de quem escreve: "isto já
 * veio pronto?", "falta eu escrever?", "isto foi a IA que escreveu?", "eu mexi
 * aqui?", "a fonte não tinha o que dizer?" e "isto está travado pela alçada?".
 * Fundi-los em "ok / pendente" devolveria a mesma cor para o bloco que a IA
 * acabou de escrever e para o que ninguém leu.
 */
export const PROPOSAL_DRAFT_BLOCK_STATE = [
  'AUTOMATICO',
  'AGUARDANDO',
  /**
   * CERES-041 — o texto veio do BRIEFING da oportunidade, e ninguém o reescreveu.
   *
   * Estado próprio, e não `EDITADO`: "uma pessoa escreveu isto aqui" e "isto foi
   * copiado do que a oportunidade já dizia" são fatos diferentes, e quem revisa
   * a proposta precisa saber qual dos dois está lendo.
   */
  'DO_BRIEFING',
  'SUGESTAO_IA',
  'EDITADO',
  'SEM_INFORMACAO',
  'BLOQUEADO',
  /**
   * CERES-PRP-011 — o agente de propostas montou o texto a partir das fontes do
   * CRM (Catálogo, Playbook, conteúdo institucional) e ninguém o revisou ainda.
   */
  'DAS_FONTES',
] as const;
export type ProposalDraftBlockState = (typeof PROPOSAL_DRAFT_BLOCK_STATE)[number];

/**
 * O que impede FECHAR a versão, e o que apenas avisa.
 *
 * `BLOQUEIA` some da lista quando a causa é resolvida; `AVISA` continua lá e
 * não trava nada. A distinção é do servidor: uma tela que decide sozinha o que
 * é bloqueio acaba oferecendo o botão que a API vai recusar.
 */
export const PROPOSAL_DRAFT_ISSUE_SEVERITY = ['BLOQUEIA', 'AVISA'] as const;
export type ProposalDraftIssueSeverity = (typeof PROPOSAL_DRAFT_ISSUE_SEVERITY)[number];

/** Por que a assistência de IA não está disponível agora. */
export const PROPOSAL_AI_AVAILABILITY = ['DISPONIVEL', 'NAO_CONFIGURADA', 'INDISPONIVEL'] as const;
export type ProposalAiAvailability = (typeof PROPOSAL_AI_AVAILABILITY)[number];

// ===========================================================================
// CERES-041 — O BRIEFING DA PROPOSTA, NA OPORTUNIDADE
// ===========================================================================

/**
 * A situação do briefing, CALCULADA a partir do preenchimento.
 *
 * Nunca editada à mão: uma situação que se escolhe vira rótulo de vontade, e
 * não de fato — e a tela que mostra "Pronto para proposta" sobre um briefing
 * vazio é pior do que tela nenhuma.
 */
export const OPPORTUNITY_BRIEFING_STATUS = [
  'NAO_INICIADO',
  'EM_PREENCHIMENTO',
  'PRONTO_PARA_PROPOSTA',
  /** Mudou depois de uma proposta ter sido gerada a partir dele. */
  'DESATUALIZADO',
] as const;
export type OpportunityBriefingStatus = (typeof OPPORTUNITY_BRIEFING_STATUS)[number];

/**
 * De onde veio cada conteúdo do briefing.
 *
 * É o que a §6 da missão pede que a tela diga sem expor id nem nome técnico. O
 * vínculo com o Playbook morre na cópia: depois dela, o conteúdo é da
 * oportunidade, e mudar o Playbook não mexe em negociação nenhuma.
 */
export const BRIEFING_CONTENT_ORIGIN = [
  /** Copiado do Playbook e mantido como estava. */
  'PLAYBOOK',
  /** Veio do Playbook e foi alterado nesta oportunidade. */
  'ADAPTADO',
  /** Escrito nesta oportunidade, do zero. */
  'OPORTUNIDADE',
] as const;
export type BriefingContentOrigin = (typeof BRIEFING_CONTENT_ORIGIN)[number];

export const BRIEFING_CONTENT_ORIGIN_LABEL: Record<BriefingContentOrigin, string> = {
  PLAYBOOK: 'Padrão do Playbook',
  ADAPTADO: 'Adaptado nesta oportunidade',
  OPORTUNIDADE: 'Criado nesta oportunidade',
};

/**
 * CERES-041 — como um bloco se apresenta quando a proposta tem mais de um
 * serviço.
 *
 * `POR_SERVICO` faz o bloco virar uma seção por item negociado, com o nome do
 * serviço no título. `CONSOLIDADA` mantém o comportamento que sempre houve —
 * uma seção só, falando de tudo. O padrão é o antigo: modelo já publicado não
 * pode mudar de forma porque uma missão nova nasceu.
 */
export const PROPOSAL_BLOCK_PRESENTATION = ['CONSOLIDADA', 'POR_SERVICO'] as const;
export type ProposalBlockPresentation = (typeof PROPOSAL_BLOCK_PRESENTATION)[number];

// ===========================================================================
// CERES-043 — O ENVIO DA PROPOSTA POR E-MAIL
// ===========================================================================

/**
 * O estado de UM pedido de envio.
 *
 * Quatro estados porque são quatro fatos distintos, e cada um muda o que a tela
 * oferece: o pedido foi registrado (`QUEUED`), está em curso (`SENDING`), o
 * provedor aceitou (`SENT`) ou recusou (`FAILED`).
 *
 * **`SENT` significa "aceito pelo provedor", e não "entregue".** Entrega,
 * abertura e leitura são assíncronas e ficam fora desta missão — chamar de
 * entregue o que o provedor apenas aceitou é a mentira mais fácil de contar
 * numa tela de envio, e a mais cara de descobrir.
 */
export const PROPOSAL_SEND_STATUS = [
  'QUEUED',
  'SENDING',
  'SENT',
  'FAILED',
  /**
   * CERES-044 — o provedor PODE ter aceitado, e não se sabe.
   *
   * Nasce quando a conexão cai, o tempo esgota ou o processo encerra entre a
   * chamada e a resposta. Não vira falha sozinho: repetir sem conferir manda a
   * mesma proposta duas vezes ao cliente.
   */
  'INCERTO',
] as const;
export type ProposalSendStatus = (typeof PROPOSAL_SEND_STATUS)[number];

export const PROPOSAL_SEND_STATUS_LABEL: Record<ProposalSendStatus, string> = {
  QUEUED: 'Enfileirado',
  SENDING: 'Enviando',
  SENT: 'Enviado ao provedor',
  FAILED: 'Falhou',
  INCERTO: 'Resultado incerto',
};

/**
 * Por que um envio falhou, em categorias que mudam o que fazer a seguir.
 *
 * A categoria é o que a tela mostra; a mensagem do provedor, quando existe,
 * entra saneada. Nenhuma delas carrega token, host interno ou resposta crua —
 * quem lê a ficha da proposta é quem vende.
 */
export const PROPOSAL_SEND_FAILURE = [
  'SEM_CONFIGURACAO',
  'DESTINATARIO_INVALIDO',
  'ARQUIVO_INDISPONIVEL',
  'AUTENTICACAO',
  'LIMITE',
  'INDISPONIVEL',
  'NAO_CLASSIFICADA',
] as const;
export type ProposalSendFailure = (typeof PROPOSAL_SEND_FAILURE)[number];

export const PROPOSAL_SEND_FAILURE_LABEL: Record<ProposalSendFailure, string> = {
  SEM_CONFIGURACAO: 'Envio de e-mail não configurado',
  DESTINATARIO_INVALIDO: 'Destinatário recusado',
  ARQUIVO_INDISPONIVEL: 'Arquivo da proposta indisponível',
  AUTENTICACAO: 'Falha de autenticação com o provedor',
  LIMITE: 'Limite do provedor atingido',
  INDISPONIVEL: 'Provedor indisponível no momento',
  NAO_CLASSIFICADA: 'Falha não classificada',
};

/** A situação da configuração de e-mail, como a tela a mostra. */
export const EMAIL_CONFIG_STATUS = ['CONFIGURADO', 'NAO_CONFIGURADO', 'REQUER_ATENCAO'] as const;
export type EmailConfigStatus = (typeof EMAIL_CONFIG_STATUS)[number];

/** Os limites do envio. Existem para o cabeçalho não virar lista de mala direta. */
export const PROPOSAL_SEND_LIMITES = {
  destinatarios: 20,
  assunto: 200,
  corpo: 20_000,
} as const;

// ===========================================================================
// CERES-044 — ACOMPANHAMENTO E DESFECHO
// ===========================================================================

/** Por onde o acompanhamento aconteceu. Os mesmos canais da atividade. */
export const PROPOSAL_FOLLOWUP_CHANNEL = ['EMAIL', 'CALL', 'WHATSAPP', 'MEETING', 'OTHER'] as const;
export type ProposalFollowUpChannel = (typeof PROPOSAL_FOLLOWUP_CHANNEL)[number];

export const PROPOSAL_FOLLOWUP_CHANNEL_LABEL: Record<ProposalFollowUpChannel, string> = {
  EMAIL: 'E-mail',
  CALL: 'Telefone',
  WHATSAPP: 'WhatsApp',
  MEETING: 'Reunião',
  OTHER: 'Outro',
};

/** O que se combinou de fazer a seguir. */
export const PROPOSAL_NEXT_ACTION_TYPE = [
  'CONTATO_DE_ACOMPANHAMENTO',
  'REUNIAO',
  'REVISAO_DA_PROPOSTA',
  'AGUARDAR_RETORNO',
  'NEGOCIAR_CONDICOES',
  'ENVIAR_NOVA_VERSAO',
  'OUTRO',
] as const;
export type ProposalNextActionType = (typeof PROPOSAL_NEXT_ACTION_TYPE)[number];

export const PROPOSAL_NEXT_ACTION_TYPE_LABEL: Record<ProposalNextActionType, string> = {
  CONTATO_DE_ACOMPANHAMENTO: 'Contato de acompanhamento',
  REUNIAO: 'Reunião',
  REVISAO_DA_PROPOSTA: 'Revisão da proposta',
  AGUARDAR_RETORNO: 'Aguardar retorno',
  NEGOCIAR_CONDICOES: 'Negociar condições',
  ENVIAR_NOVA_VERSAO: 'Enviar nova versão',
  OUTRO: 'Outro',
};

/**
 * Por que o cliente recusou.
 *
 * Lista CONTROLADA porque a pergunta que ela responde é de leitura agregada:
 * "por que estamos perdendo?". Texto livre responde uma proposta de cada vez e
 * nenhuma pergunta sobre o conjunto.
 */
export const PROPOSAL_REJECTION_REASON = [
  'PRECO',
  'PRAZO',
  'ESCOPO',
  'PRIORIDADE_ALTERADA',
  'CONCORRENTE_ESCOLHIDO',
  'ORCAMENTO_INDISPONIVEL',
  'PROJETO_CANCELADO',
  'SEM_RETORNO',
  'OUTRO',
] as const;
export type ProposalRejectionReason = (typeof PROPOSAL_REJECTION_REASON)[number];

export const PROPOSAL_REJECTION_REASON_LABEL: Record<ProposalRejectionReason, string> = {
  PRECO: 'Preço',
  PRAZO: 'Prazo',
  ESCOPO: 'Escopo',
  PRIORIDADE_ALTERADA: 'Prioridade alterada',
  CONCORRENTE_ESCOLHIDO: 'Concorrente escolhido',
  ORCAMENTO_INDISPONIVEL: 'Orçamento indisponível',
  PROJETO_CANCELADO: 'Projeto cancelado',
  SEM_RETORNO: 'Sem retorno',
  OUTRO: 'Outro',
};

/** O que a lista e a ficha sinalizam. Chave fechada; a tela traduz. */
export const PROPOSAL_ALERT = [
  'ENVIADA_SEM_PROXIMA_ACAO',
  'PROXIMA_ACAO_ATRASADA',
  'VALIDADE_PROXIMA',
  'VALIDADE_VENCIDA',
  'ENVIO_INCERTO',
  'VERSAO_NAO_ENVIADA',
  'OPORTUNIDADE_ALTERADA',
] as const;
export type ProposalAlert = (typeof PROPOSAL_ALERT)[number];

/** O tipo de cada entrada da linha do tempo. */
export const PROPOSAL_TIMELINE_KIND = [
  'REGISTRO',
  'VERSAO',
  'ENVIO',
  'ACOMPANHAMENTO',
  'PROXIMA_ACAO',
  'DESFECHO',
] as const;
export type ProposalTimelineKind = (typeof PROPOSAL_TIMELINE_KIND)[number];
